import React, { FC, useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { IconToggle, Menu, Popup } from "@evergis/ui";
import { useTranslation } from "react-i18next";
import { useMapViewActions } from "@evergis/react";
import { IconTypesKeys } from "@evergis/ui/dist/atoms/Icon";
import { IOption } from "@evergis/ui/dist/organisms/Menu/types";
import { EnvelopeDc, ResourceTypeLink } from "@evergis/api";

import { FeatureCardContextMenuContainer, DeleteFeauturesConfirmContainer } from "./styled";

import { i18n } from "../../../../i18n";
import { layerExtent } from "../../../../resources/layers";

import { getSelectedFeaturesTotalCounts } from "../../../../ducks/selectFeatures/selectors";
import { useToggle } from "../../../../hooks/useToggle";
import { useLimitsExceededCheck } from "../../../../hooks/useLimitsExceededCheck";
import { useConfirmDialog, useCopyResourceDialog, useExportDialog } from "../../../../hooks/useDialogs";
import { useSelectFeaturesLayerName } from "../../../../hooks/selectFeatures/useSelectFeaturesLayerName";
import { useDeleteFeatures } from "../../../Table/hooks/useDeleteFeatures";
import { useFeatureCard } from "../../hooks/useFeatureCard";
import { useProjectSrid } from "../../../../ducks/project/hooks";
import { useTempLayerConditions } from "../../../../hooks/layer/useTempLayerConditions";
import { useIsShared } from "../../../../hooks/api/useIsShared";

import { ContextMenuActions, FeatureCardContextMenuProps } from "./types";
import { ConfirmDialogOptions } from "../../../../components/Dialogs/Confirmation/Confirmation";

const CONTEXT_MENU_OPTIONS = [
  {
    icon: "zoom_to" as IconTypesKeys,
    text: i18n.t("zoomTo", { ns: "featureCard" }),
    value: ContextMenuActions.ZoomTo,
  },
  {
    icon: "download" as IconTypesKeys,
    text: i18n.t("export", { ns: "featureCard" }),
    value: ContextMenuActions.Export,
  },
  {
    icon: "copy" as IconTypesKeys,
    text: i18n.t("copy", { ns: "featureCard" }),
    value: ContextMenuActions.Copy,
  },
  {
    icon: "delete" as IconTypesKeys,
    text: i18n.t("delete", { ns: "featureCard" }),
    value: ContextMenuActions.Delete,
  },
];

const SHARED_MENU_ITEMS = ["zoom_to"];

export const FeatureCardContextMenu: FC<FeatureCardContextMenuProps> = ({ layerInfo, disabled }) => {
  const { t } = useTranslation();
  const [menuOpen, toggleMenuOpen] = useToggle(false);
  const [layerName] = useSelectFeaturesLayerName();
  const openCopyResourceDialog = useLimitsExceededCheck(useCopyResourceDialog());
  const openExportDialog = useExportDialog();
  const confirmDialog = useConfirmDialog();
  const deleteFeatures = useDeleteFeatures(layerInfo);
  const totalCounts = useSelector(getSelectedFeaturesTotalCounts);
  const [, { closeCard, resetSelected }] = useFeatureCard();
  const spatialReference = useProjectSrid();
  const tempConditions = useTempLayerConditions(false);
  const { animateTo } = useMapViewActions();
  const isShared = useIsShared();
  const [getLayerExtent, { data: extentLayer, loading }] = layerExtent.use();

  const onZoomToFeatures = useCallback(async () => {
    await getLayerExtent({
      input: {
        name: layerName,
        sr: spatialReference,
        condition: tempConditions[layerName],
      },
    });
  }, [layerName, getLayerExtent, spatialReference, tempConditions]);

  const onOpenExportDialog = useCallback(
    () => openExportDialog([layerInfo?.name, layerInfo?.alias, ResourceTypeLink.Layer], { fromFeatureCard: true }),
    [openExportDialog, layerInfo],
  );

  const onOpenCopyDialog = useCallback(
    () => openCopyResourceDialog([layerName, ResourceTypeLink.Layer], { fromFeatureCard: true }),
    [layerName, openCopyResourceDialog],
  );

  const onDeleteOption = useCallback(() => {
    confirmDialog([""], {
      title: t("objectsDeletion"),
      content: (
        <DeleteFeauturesConfirmContainer>
          <div>
            {`${t("objectsDeletionConfirm1")} (${totalCounts?.[layerName]}) ${t("fromLayer")}`}{" "}
            <strong>{layerInfo?.alias || layerInfo?.name}</strong>?
          </div>
          <br />
          <div>{t("objectsDeletionConfirm2")}.</div>
        </DeleteFeauturesConfirmContainer>
      ),
      onConfirm: () => {
        deleteFeatures().then(() => {
          closeCard();
          resetSelected();
        });
      },
      options: {
        maxWidth: "40rem",
      },
    } as ConfirmDialogOptions);
  }, [
    closeCard,
    confirmDialog,
    deleteFeatures,
    layerInfo?.alias,
    layerInfo?.name,
    layerName,
    resetSelected,
    t,
    totalCounts,
  ]);

  const onSelectOption = useCallback(
    (option: IOption) => {
      switch (option.value) {
        case ContextMenuActions.ZoomTo:
          onZoomToFeatures();

          break;
        case ContextMenuActions.Export:
          onOpenExportDialog();

          break;
        case ContextMenuActions.Copy:
          onOpenCopyDialog();

          break;
        case ContextMenuActions.Delete:
          onDeleteOption();

          break;
        default:
      }

      toggleMenuOpen();
    },
    [onZoomToFeatures, onOpenExportDialog, onOpenCopyDialog, onDeleteOption, toggleMenuOpen],
  );

  const menuOptions = useMemo(
    () =>
      isShared ? CONTEXT_MENU_OPTIONS.filter(({ icon }) => SHARED_MENU_ITEMS.includes(icon)) : CONTEXT_MENU_OPTIONS,
    [isShared],
  );

  useEffect(() => {
    if (extentLayer && !loading) {
      const {
        coordinates: [[xMin, yMin], [xMax, yMax]],
      } = extentLayer as EnvelopeDc;

      if (xMin || yMin || xMax || yMax) {
        animateTo({
          extent: { xMax, xMin, yMax, yMin },
        });
      }
    }
  }, [extentLayer, loading]); // eslint-disable-line

  return (
    <Popup
      zIndex={101}
      anchor={ref => (
        <IconToggle
          kind="menu_vertical"
          innerRef={ref}
          disabled={disabled}
          isSelected={menuOpen}
          onClick={toggleMenuOpen}
        />
      )}
      isVisible={menuOpen}
      onRequestClose={toggleMenuOpen}
    >
      <FeatureCardContextMenuContainer>
        <Menu options={menuOptions} selectOption={(_, item) => onSelectOption(item as IOption)} />
      </FeatureCardContextMenuContainer>
    </Popup>
  );
};
